/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import {
  SYNCGROUPS_TYPE_SETBBOX,
  SYNCGROUPS_TYPE_SETFEATURES,
  SYNCGROUPS_TYPE_SETTIME,
} from './constants';
import { syncGroupsActions } from './reducer';
import { createSyncGroupViewStateSelector } from './selector';
import { getSynchronizationGroup } from './selectors';
import type {
  SyncGroupAddTargetPayload,
  SyncGroupLinkTargetPayload,
  SynchronizationGroupState,
  SynchronizationGroupModuleState,
} from './types';
import { setBboxSync, setTimeSync } from '../synchronizationActions/actions';
import type { SetBboxPayload, SetTimePayload } from '../types';

export const syncGroupsListener =
  createListenerMiddleware<SynchronizationGroupModuleState>();

const FEATURE_GROUP_PREFIX = 'Features_';

const getFeatureGroupId = (panelId: string): string =>
  `${FEATURE_GROUP_PREFIX}${panelId}`;

const getPanelIdFromFeatureGroup = (groupId: string): string | undefined =>
  groupId.startsWith(FEATURE_GROUP_PREFIX)
    ? groupId.slice(FEATURE_GROUP_PREFIX.length)
    : undefined;

const isFeatureSource = (
  source: SynchronizationGroupState['sources']['byId'][string] | undefined,
): boolean => !!source?.types.includes(SYNCGROUPS_TYPE_SETFEATURES);

const isMapSource = (
  source: SynchronizationGroupState['sources']['byId'][string] | undefined,
): boolean =>
  !!source?.types.includes(SYNCGROUPS_TYPE_SETBBOX) &&
  !!source?.types.includes(SYNCGROUPS_TYPE_SETTIME);

const getMapSourceIds = (state: SynchronizationGroupState): Set<string> =>
  new Set(
    state.sources.allIds.filter((id) => isMapSource(state.sources.byId[id])),
  );

const getFeatureSourceIds = (state: SynchronizationGroupState): string[] =>
  state.sources.allIds.filter((id) => isFeatureSource(state.sources.byId[id]));

const ensureFeatureGroupsForSources = (
  state: SynchronizationGroupState,
  dispatch: (action: unknown) => void,
): void => {
  const featureSourceIds = getFeatureSourceIds(state);

  featureSourceIds.forEach((panelId) => {
    const groupId = getFeatureGroupId(panelId);
    if (!state.groups.byId[groupId]) {
      dispatch(
        syncGroupsActions.syncGroupAddGroup({
          groupId,
          title: 'Features',
          type: SYNCGROUPS_TYPE_SETFEATURES,
        }),
      );
    }
  });

  state.groups.allIds.forEach((groupId) => {
    const group = state.groups.byId[groupId];
    if (group?.type !== SYNCGROUPS_TYPE_SETFEATURES) {
      return;
    }
    const panelId = getPanelIdFromFeatureGroup(groupId);
    if (!panelId || !featureSourceIds.includes(panelId)) {
      dispatch(syncGroupsActions.syncGroupRemoveGroup({ groupId }));
    }
  });
};

const pruneNonMapTargetsFromFeatureGroups = (
  state: SynchronizationGroupState,
  dispatch: (action: unknown) => void,
): void => {
  const mapSourceIds = getMapSourceIds(state);
  state.groups.allIds.forEach((groupId) => {
    const group = state.groups.byId[groupId];
    if (group?.type !== SYNCGROUPS_TYPE_SETFEATURES) {
      return;
    }

    const panelId = getPanelIdFromFeatureGroup(groupId);
    const linkedTargetIds = new Set(
      panelId ? (state.linkedState.links[panelId] ?? []) : [],
    );

    group.targets.allIds.forEach((targetId) => {
      if (!mapSourceIds.has(targetId) && !linkedTargetIds.has(targetId)) {
        dispatch(
          syncGroupsActions.syncGroupRemoveTarget({
            groupId,
            targetId,
          }),
        );
      }
    });
  });
};

const applyLegacyLinksToFeatureGroups = (
  state: SynchronizationGroupState,
  dispatch: (action: unknown) => void,
): void => {
  const featureSourceIds = getFeatureSourceIds(state);

  featureSourceIds.forEach((panelId) => {
    const groupId = getFeatureGroupId(panelId);
    const group = state.groups.byId[groupId];
    if (!group) {
      return;
    }

    const linkedTargetIds = state.linkedState.links[panelId] ?? [];

    linkedTargetIds.forEach((targetId) => {
      if (!group.targets.allIds.includes(targetId)) {
        dispatch(
          syncGroupsActions.syncGroupAddTarget({
            groupId,
            targetId,
            linked: true,
          }),
        );
      } else if (!group.targets.byId[targetId]?.linked) {
        dispatch(
          syncGroupsActions.syncGroupLinkTarget({
            groupId,
            targetId,
            linked: true,
          }),
        );
      }
    });

    group.targets.allIds.forEach((targetId) => {
      if (!linkedTargetIds.includes(targetId)) {
        dispatch(
          syncGroupsActions.syncGroupRemoveTarget({
            groupId,
            targetId,
          }),
        );
      }
    });
  });
};

const deriveLegacyLinksFromFeatureGroups = (
  state: SynchronizationGroupState,
): Record<string, string[]> => {
  const featureSourceIds = getFeatureSourceIds(state);
  const links: Record<string, string[]> = {};

  Object.entries(state.linkedState.links).forEach(([panelId, linkedMapIds]) => {
    const source = state.sources.byId[panelId];
    if (panelId === 'warning-dialog' || source) {
      return;
    }
    links[panelId] = linkedMapIds;
  });

  featureSourceIds.forEach((panelId) => {
    const groupId = getFeatureGroupId(panelId);
    const group = state.groups.byId[groupId];
    if (group?.type !== SYNCGROUPS_TYPE_SETFEATURES) {
      links[panelId] = state.linkedState.links[panelId] ?? [];
      return;
    }

    const derivedTargetIds = group.targets.allIds.filter(
      (targetId) => group.targets.byId[targetId]?.linked,
    );

    links[panelId] = Array.from(new Set(derivedTargetIds));
  });

  return links;
};

const reconcileFeatureGroups = (
  state: SynchronizationGroupState,
  dispatch: (action: unknown) => void,
): void => {
  ensureFeatureGroupsForSources(state, dispatch);
  pruneNonMapTargetsFromFeatureGroups(state, dispatch);
  applyLegacyLinksToFeatureGroups(state, dispatch);
};

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddTarget,
    syncGroupsActions.syncGroupLinkTarget,
  ),
  effect: async ({ payload, type }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const {
      groupId,
      linked,
      targetId: targetToUpdate,
    } = payload as SyncGroupAddTargetPayload | SyncGroupLinkTargetPayload;

    const group = getSynchronizationGroup(listenerApi.getState(), groupId);

    if (!linked && group) {
      switch (group.type) {
        case SYNCGROUPS_TYPE_SETTIME:
          // A local panel should keep its own current time after unlinking from timeslider sync.
          if (type === syncGroupsActions.syncGroupLinkTarget.type) {
            break;
          }
          if (
            !group.targets.allIds.some(
              (targetId) =>
                targetId !== targetToUpdate &&
                group.targets.byId[targetId]?.linked,
            )
          ) {
            break;
          }
          if (!group.payloadByType[SYNCGROUPS_TYPE_SETTIME]) {
            return;
          }
          listenerApi.dispatch(
            setTimeSync(
              group.payloadByType[SYNCGROUPS_TYPE_SETTIME] as SetTimePayload,
              [
                {
                  targetId: targetToUpdate,
                  value: (
                    group.payloadByType[
                      SYNCGROUPS_TYPE_SETTIME
                    ] as SetTimePayload
                  ).value,
                },
              ],
              [groupId],
            ),
          );
          break;
        case SYNCGROUPS_TYPE_SETBBOX:
          if (!group.payloadByType[SYNCGROUPS_TYPE_SETBBOX]) {
            return;
          }
          listenerApi.dispatch(
            setBboxSync(
              group.payloadByType[SYNCGROUPS_TYPE_SETBBOX] as SetBboxPayload,
              [
                {
                  targetId: targetToUpdate,
                  bbox: (
                    group.payloadByType[
                      SYNCGROUPS_TYPE_SETBBOX
                    ] as SetBboxPayload
                  ).bbox,
                  srs: (
                    group.payloadByType[
                      SYNCGROUPS_TYPE_SETBBOX
                    ] as SetBboxPayload
                  ).srs,
                },
              ],
              [groupId],
            ),
          );
          break;
        default:
          break;
      }
    }
  },
});

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddGroup,
    syncGroupsActions.syncGroupRemoveGroup,
    syncGroupsActions.syncGroupAddSource,
    syncGroupsActions.syncGroupRemoveSource,
    syncGroupsActions.setLinkedMap,
    syncGroupsActions.updateLinkedMap,
  ),
  effect: async (_, listenerApi) => {
    const state = listenerApi.getState().syncGroups;
    if (!state) {
      return;
    }
    reconcileFeatureGroups(state, listenerApi.dispatch);
  },
});

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddTarget,
    syncGroupsActions.syncGroupRemoveTarget,
    syncGroupsActions.syncGroupLinkTarget,
    syncGroupsActions.syncGroupRemoveGroup,
    syncGroupsActions.syncGroupRemoveSource,
  ),
  effect: async (action, listenerApi) => {
    const actionPayload =
      'payload' in action &&
      action.payload &&
      typeof action.payload === 'object'
        ? (action.payload as {
            groupId?: string;
          })
        : undefined;

    if (
      actionPayload?.groupId &&
      !actionPayload.groupId?.startsWith(FEATURE_GROUP_PREFIX) &&
      !syncGroupsActions.syncGroupRemoveSource.match(action)
    ) {
      return;
    }

    const state = listenerApi.getState().syncGroups;
    if (!state) {
      return;
    }

    const linksFromFeatureGroups = deriveLegacyLinksFromFeatureGroups(state);
    if (!isEqual(linksFromFeatureGroups, state.linkedState.links)) {
      listenerApi.dispatch(
        syncGroupsActions.setLinkedMap({ newLinks: linksFromFeatureGroups }),
      );
    }
  },
});

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddGroup,
    syncGroupsActions.syncGroupRemoveGroup,
    syncGroupsActions.syncGroupAddSource,
    syncGroupsActions.syncGroupRemoveSource,
    syncGroupsActions.syncGroupAddTarget,
    syncGroupsActions.syncGroupRemoveTarget,
    syncGroupsActions.syncGroupLinkTarget,
  ),
  effect: async (_, listenerApi) => {
    const viewState = createSyncGroupViewStateSelector(listenerApi.getState());
    const currentViewState = listenerApi.getState().syncGroups?.viewState;
    if (!currentViewState || !isEqual(currentViewState, viewState)) {
      listenerApi.dispatch(
        syncGroupsActions.syncGroupSetViewState({ viewState }),
      );
    }
  },
});
